import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../BonusRewardsSection.module.scss';
import BonusPointsCard from '~/shared/components/BonusPointsCard/BonusPointsCard';
import { CTA } from '~/shared/components/ProductCard/components/CTA';
import { useAddRewardToBasket, useRemoveRewardFromBasket } from '~/shared/hooks/loyalty';
import CloseIcon from '~/icons/close.svg';
import Typography from '~/shared/components/Typography/Typography';
import { Reward } from '~/lib/data-contract/loyalty';
import { useTranslation } from '~/shared/utils/translation';
import { usePaymentDetails } from '~/features/commerce-api/hooks/usePaymentDetails';
import { useBasket } from '~/features/basket/hooks';
import { useLocalBasketState } from '~/features/basket/hooks/useLocalBasketState';
import { useMutationState } from '@tanstack/react-query';
import { MutationKeys } from '~/services/loyalty/constants';
import { extractRewardsStatusByBarcode } from '~/services/loyalty/lib';
import { PaymentMethod } from '~/shared/constants';

type RewardItem = Pick<Reward, 'status' | 'value' | 'lastvaliddate' | 'couponNo' | 'barcode'>;

export const BonusRewardsList = ({ memberRewards = [] }: { memberRewards?: RewardItem[] }) => {
    const [showAll, setShowAll] = useState(false);
    const extraCardRef = useRef<HTMLDivElement | null>(null);
    const { translate } = useTranslation();
    const { mutate: addReward } = useAddRewardToBasket();
    const { mutate: removeReward } = useRemoveRewardFromBasket();
    const basket = useBasket();
    const { membership } = useLocalBasketState();
    const { remainingTotal } = usePaymentDetails(basket, membership);

    const rewardBarcodesAddedToBasket = useMemo(() => {
        extractRewardsStatusByBarcode(basket?.paymentInstruments, PaymentMethod.LOYALTY).map(
            (reward) => reward.barcode,
        );
    }, [basket?.paymentInstruments]);

    useEffect(() => {
        if (showAll && extraCardRef.current) {
            extraCardRef.current.focus();
        }
    }, [showAll]);

    const pendingRewardBarcodes = useMutationState({
        filters: { mutationKey: [MutationKeys.ADD_REWARD], status: 'pending' },
        // Cast is needed because TanStack types `variables` as unknown.
        select: (mutation) =>
            (mutation.state.variables as { rewardCode: string } | undefined)?.rewardCode,
    });

    const pendingRewardCancellations = useMutationState({
        filters: { mutationKey: [MutationKeys.REMOVE_REWARD], status: 'pending' },
        select: (mutation) => mutation.state.variables,
    });

    const sortedRewards = useMemo(() => {
        return [...memberRewards].sort((a, b) => {
            //@ts-ignore
            const aInBasket = rewardBarcodesAddedToBasket.includes(a.barcode);
            //@ts-ignore
            const bInBasket = rewardBarcodesAddedToBasket.includes(b.barcode);
            if (aInBasket && !bInBasket) return -1;
            if (!aInBasket && bInBasket) return 1;
            return 0;
        });
    }, [memberRewards]);

    const visibleRewards = showAll ? sortedRewards : sortedRewards.slice(0, 2);

    return (
        <div className={styles.accordionContent}>
            <div className={styles.titleInfoWrapper}>
                <h4 className={styles.title}>{translate('basket.bonusRewardsTitle')}</h4>
                <p className={styles.info} role="note">
                    {translate('basket.bonusRewardsInfo')}
                </p>
            </div>
            <div className={`${showAll ? styles.expanded : styles.collapsed}`}>
                {visibleRewards?.map(({ couponNo, value, lastvaliddate, barcode }, index) => {
                    const isFirstExtra = index === 2 && showAll;
                    const isPendingAdd = pendingRewardBarcodes.includes(barcode);

                    const isPendingCancel = pendingRewardCancellations.includes(barcode);
                    //@ts-ignore
                    const isAddedToBasket = rewardBarcodesAddedToBasket.includes(barcode);

                    return (
                        <React.Fragment key={couponNo}>
                            <div
                                aria-label={`Du er på nummer ${index + 1} reward ud af ${memberRewards?.length}. Denne reward er ${
                                    isAddedToBasket
                                        ? translate('basket.redeemed')
                                        : `ikke ${translate('basket.redeemed')}`
                                }`}
                                className={styles.bonusRewardsWrapper}
                                ref={isFirstExtra ? extraCardRef : null}
                            >
                                <BonusPointsCard
                                    amount={value}
                                    expiryDate={lastvaliddate}
                                    variant="small"
                                />
                                <CTA
                                    aria-label={`${translate('basket.redeem')} brugen af nummer ${index + 1} reward til en værdi af ${value} kroner ved at trykke på denne knap`}
                                    className={styles.cta}
                                    disabled={
                                        isAddedToBasket || isPendingAdd || remainingTotal === 0
                                    }
                                    text={
                                        isPendingAdd
                                            ? translate('basket.redeeming')
                                            : isAddedToBasket
                                              ? translate('basket.redeemed')
                                              : translate('basket.redeem')
                                    }
                                    onClick={() =>
                                        addReward({ rewardCode: barcode, amount: value })
                                    }
                                />
                            </div>
                            {isAddedToBasket && (
                                <div className={styles.activeRewardInfoWrapper}>
                                    <Typography variant="paragraph-S" as="p">
                                        {translate('basket.addedAsDiscount')}
                                    </Typography>

                                    <button
                                        aria-label={`${translate('basket.cancel')} brugen af nummer ${index + 1} reward til en værdi af ${value} kroner ved at trykke på denne knap`}
                                        onClick={() => removeReward(barcode)}
                                        className={styles.activeButton}
                                        type="button"
                                        disabled={isPendingCancel}
                                    >
                                        <Typography variant="paragraph-S" as="span">
                                            {isPendingCancel
                                                ? translate('basket.cancelling')
                                                : translate('basket.cancel')}
                                        </Typography>

                                        <CloseIcon height={14} width={10} />
                                    </button>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
            {sortedRewards.length > 2 && (
                <CTA
                    aria-label={
                        showAll ? 'Vis færre rewards' : `Vis alle ${memberRewards?.length} rewards`
                    }
                    variant="plain"
                    text={showAll ? 'Vis færre' : `Vis alle (${memberRewards?.length})`}
                    onClick={() => setShowAll((prev) => !prev)}
                />
            )}
        </div>
    );
};
